/**
 * Decodes a raw ODM <FormData> instance into a `patient_form_submissions`
 * payload keyed the same way the eCRF renderer keys its responses (field.id),
 * with coded values resolved to their human labels.
 *
 * Also pulls out the three things the CTMS needs alongside the answers:
 * the REDCap `*_complete` flag, the visit date and the visit number.
 *
 * Pure functions — no Supabase, no React.
 */
import type { OdmFormImport, OdmImportResult, OdmFormField } from './odmImporter';
import type { OdmFormInstance } from './odmClinicalStream';

export type RedcapCompleteFlag = 0 | 1 | 2 | null;

/** REDCap completion flag -> BRIDGE form_status. */
export function statusFromCompleteFlag(flag: RedcapCompleteFlag): 'draft' | 'submitted' {
  // 0 = Incomplete -> draft, 1 = Unverified (in progress) -> draft,
  // 2 = Complete -> submitted. The raw flag is always preserved in _source.
  return flag === 2 ? 'submitted' : 'draft';
}

export function completeFlagLabel(flag: RedcapCompleteFlag): string {
  switch (flag) {
    case 0:
      return 'Incomplete';
    case 1:
      return 'In progress (unverified)';
    case 2:
      return 'Complete';
    default:
      return 'Not recorded';
  }
}

interface FormDecoder {
  form: OdmFormImport;
  /** ItemOID -> field (non-checkbox). */
  byItemOid: Map<string, OdmFormField>;
  /** REDCap checkbox variable -> field. */
  byCheckboxVariable: Map<string, OdmFormField>;
}

export interface OdmDecoder {
  byFormOid: Map<string, FormDecoder>;
  byFormName: Map<string, FormDecoder>;
}

export function buildOdmDecoder(meta: OdmImportResult): OdmDecoder {
  const byFormOid = new Map<string, FormDecoder>();
  const byFormName = new Map<string, FormDecoder>();

  for (const form of meta.forms) {
    const byItemOid = new Map<string, OdmFormField>();
    const byCheckboxVariable = new Map<string, OdmFormField>();
    for (const field of form.schema.fields) {
      if (field.redcap_field_type === 'checkbox') {
        byCheckboxVariable.set(field.redcap_variable || field.odm_item_oid || field.id, field);
      } else if (field.odm_item_oid) {
        byItemOid.set(field.odm_item_oid, field);
      }
    }
    const decoder: FormDecoder = { form, byItemOid, byCheckboxVariable };
    byFormOid.set(form.odm_form_oid, decoder);
    byFormName.set(form.formName, decoder);
  }

  return { byFormOid, byFormName };
}

function labelForOption(field: OdmFormField, raw: string): string {
  if (!field.options?.length) return raw;
  const hit = field.options.find((o) => o.value === raw);
  return hit ? hit.label : raw;
}

export interface DecodedFormInstance {
  formOid: string;
  formName: string;
  /** field.id -> decoded value (label text, or array of labels for checkboxes). */
  values: Record<string, unknown>;
  /** Raw ItemOIDs that had no matching field definition. */
  unmappedItemOids: string[];
  completeFlag: RedcapCompleteFlag;
  visitDate?: string;
  visitNumber?: number;
  /** Count of answered (non-empty) items, ignoring the completion flag. */
  answeredCount: number;
}

const VISIT_DATE_KEYS = ['visit_date', 'visitdate', 'date_of_visit', 'visit_dt'];
const VISIT_NUMBER_KEYS = ['visit_number', 'visitnumber', 'visit_no', 'visit_num'];

function baseVariable(itemOid: string): string {
  const idx = itemOid.indexOf('___');
  return idx === -1 ? itemOid : itemOid.slice(0, idx);
}

function normalizeVisitDate(raw: string): string | undefined {
  const t = (raw || '').trim();
  if (!t) return undefined;
  const iso = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const slash = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    // REDCap US export: M/D/YYYY
    const [, m, d, y] = slash;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return undefined;
}

/**
 * Decode one exported form instance against the imported metadata.
 * Returns null when the form OID is unknown to the metadata.
 */
export function decodeFormInstance(
  decoder: OdmDecoder,
  instance: OdmFormInstance,
): DecodedFormInstance | null {
  const fd = decoder.byFormOid.get(instance.formOid) || decoder.byFormName.get(instance.formOid);
  if (!fd) return null;

  const values: Record<string, unknown> = {};
  const checkboxAccum = new Map<string, string[]>();
  const unmappedItemOids: string[] = [];
  let completeFlag: RedcapCompleteFlag = null;
  let visitDate: string | undefined;
  let visitNumber: number | undefined;
  let answeredCount = 0;

  for (const [itemOid, rawValue] of Object.entries(instance.items)) {
    const variable = baseVariable(itemOid);

    if (/_complete$/.test(itemOid)) {
      const n = Number(rawValue);
      if (n === 0 || n === 1 || n === 2) completeFlag = n as RedcapCompleteFlag;
      continue;
    }

    const lower = variable.toLowerCase();
    if (VISIT_DATE_KEYS.includes(lower)) visitDate = normalizeVisitDate(rawValue) ?? visitDate;
    if (VISIT_NUMBER_KEYS.includes(lower)) {
      const n = Number(String(rawValue).replace(/[^\d.-]/g, ''));
      if (Number.isFinite(n) && n > 0) visitNumber = Math.trunc(n);
    }

    // REDCap checkbox: one ItemData per choice, value "0"/"1".
    if (itemOid.includes('___')) {
      const field = fd.byCheckboxVariable.get(variable);
      if (!field) {
        unmappedItemOids.push(itemOid);
        continue;
      }
      const code = itemOid.slice(itemOid.indexOf('___') + 3);
      if (String(rawValue) === '1') {
        const list = checkboxAccum.get(field.id) || [];
        list.push(labelForOption(field, code));
        checkboxAccum.set(field.id, list);
        answeredCount++;
      } else if (!checkboxAccum.has(field.id)) {
        checkboxAccum.set(field.id, []);
      }
      continue;
    }

    const field = fd.byItemOid.get(itemOid) || fd.byCheckboxVariable.get(variable);
    if (!field) {
      if (rawValue !== '') unmappedItemOids.push(itemOid);
      continue;
    }

    if (rawValue === '') {
      values[field.id] = '';
      continue;
    }
    answeredCount++;
    values[field.id] = field.options?.length ? labelForOption(field, rawValue) : rawValue;
  }

  for (const [fieldId, labels] of checkboxAccum) values[fieldId] = labels;

  return {
    formOid: instance.formOid,
    formName: fd.form.formName,
    values,
    unmappedItemOids,
    completeFlag,
    visitDate,
    visitNumber,
    answeredCount,
  };
}

export interface LegacySourceStamp {
  system: 'redcap_odm';
  project?: string;
  study_oid?: string;
  export_timestamp?: string;
  original_form_oid: string;
  original_form_name: string;
  subject_key: string;
  repeat_key?: string;
  redcap_complete: RedcapCompleteFlag;
  redcap_complete_label: string;
  imported_from_file?: string;
  imported_at: string;
  /** Deterministic key so a re-run never duplicates a form instance. */
  import_key: string;
  unmapped_item_oids?: string[];
}

export function buildImportKey(subjectKey: string, formOid: string, repeatKey?: string): string {
  return `redcap_odm:${subjectKey}|${formOid}|${repeatKey || '1'}`;
}
